import type { ZodType } from 'zod'
import { computed, ref, watch } from 'vue'
import {
  ValidateOn,
  type FormInputValidationOptions,
} from '@/contexts/shared/form-validation/FormValidation.interfaces'
import { translateZodError } from '@/contexts/shared/form-validation/FormValidation.helpers'
import { PromiseWithResolvers } from '@/helpers/Promises.helpers'

const DEFAULT_VALIDATE_ON = ValidateOn.CHANGE

/**
 * Composable for validating a single form input against a Zod schema.
 *
 * @param value - Initial value of the input
 * @param rules - Zod schema used to validate the input
 * @param options - Validation options (initValidation, validateOn)
 *
 * @returns
 * - `input` — Reactive ref holding the current input value
 * - `errors` — Ref containing all validation error messages
 * - `error` — Computed containing only the first error message (or undefined)
 * - `validate` — Triggers async validation manually. Returns a promise that resolves when done
 * - `validated` — Returns a promise that resolves when the current validation completes or is cancelled. Useful for awaiting watcher-triggered validations
 * - `valid` — Ref indicating whether the input is currently valid
 * - `invalid` — Computed inverse of `valid`
 * - `dirty` — Ref indicating whether the input has been modified since initialization or last reset
 * - `validating` — Ref indicating whether an async validation is in progress
 * - `resetValidation` — Clears errors, sets valid to true, resets dirty and cancels pending validations
 * - `reset` — Calls resetValidation and restores the input to its initial value
 * - `onBlur` — Triggers validation if `validateOn` is set to `BLUR`
 */
export const useFormInputValidation = <T>(value: T, rules: ZodType, options: FormInputValidationOptions = {}) => {
  const _errors = ref<string[]>([])
  const _initialValue = structuredClone(value)

  const valid = ref<boolean>(true)
  const dirty = ref<boolean>(false)
  const validating = ref<boolean>(false)
  const input = ref<T>(value)

  let _skipAutoValidation = false
  let _validateVersion = 0
  let _validatedPromise = PromiseWithResolvers()
  _validatedPromise.resolve()

  const incrementValidateVersion = () => ++_validateVersion

  const validateOn = computed<ValidateOn>(() => options.validateOn ?? DEFAULT_VALIDATE_ON)

  const validate = async () => {
    const version = incrementValidateVersion()
    validating.value = true
    _validatedPromise = PromiseWithResolvers()
    const parsed = await rules.safeParseAsync(input.value, {
      errorMap: (issue) => ({ message: translateZodError(issue, options.label) }),
    })

    if (version !== _validateVersion) {
      return
    }

    valid.value = parsed.success

    if (parsed.error) {
      _errors.value = parsed.error.errors.map((error) => error.message)
    } else {
      _errors.value = []
    }
    validating.value = false
    _validatedPromise.resolve()
  }

  const resetValidation = () => {
    _validatedPromise.resolve()
    incrementValidateVersion()
    _errors.value = []
    valid.value = true
    dirty.value = false
    validating.value = false
  }

  const reset = () => {
    resetValidation()
    _skipAutoValidation = true
    input.value = structuredClone(_initialValue) as typeof input.value
    _skipAutoValidation = false
  }

  const onBlur = () => {
    if (validateOn.value === ValidateOn.BLUR) {
      return validate()
    }
  }

  const invalid = computed(() => !valid.value)

  const initValidation = computed(() => !!options.initValidation)
  const error = computed<string | undefined>(() => _errors.value[0])

  watch(
    input,
    () => {
      if (!_skipAutoValidation) {
        dirty.value = true
        if (validateOn.value === ValidateOn.CHANGE) {
          validate()
        }
      }
    },
    { flush: 'sync' }
  )

  if (initValidation.value) {
    validate()
  }

  const validated = () => _validatedPromise.promise

  return {
    input,
    errors: _errors,
    error,
    validate,
    validated,
    valid,
    invalid,
    dirty,
    validating,
    resetValidation,
    reset,
    onBlur,
  }
}
